const AREA_LABELS = {
  input: "Input",
  inputs: "Input",
  processes: "Processes",
  process: "Processes",
  culture_and_climate: "Culture and Climate",
  climate_and_culture: "Culture and Climate",
  culture: "Culture and Climate",
};

const pickFirst = (source, keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return null;
};

const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizePercent = (value) => {
  const number = toNumber(value);
  if (number === null) return null;
  return number <= 1 ? number * 100 : number;
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return null;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1"].includes(normalized)) return true;
    if (["false", "0", ""].includes(normalized)) return false;
    return null;
  }
  if (value === null || value === undefined) return false;
  return null;
};

const getNetworkName = (payload = {}) =>
  pickFirst(payload, ["network_name", "networkName", "name"]) ||
  pickFirst(payload?.network, ["name"]) ||
  "Network";

const normalizeAreaKey = (area = {}) =>
  pickFirst(area, ["key", "area_key", "areaKey", "area", "name", "id"])?.toString() || "";

const areaLabel = (key) =>
  AREA_LABELS[key?.toLowerCase()] || key?.replace(/_/g, " ") || "Uncategorized";

const normalizeAreaValue = (value) => {
  const normalized = (value || "").toString().trim().toLowerCase().replace(/[\s-]+/g, "_").replace(/&/g, "and");
  if (["input", "inputs"].includes(normalized)) return "input";
  if (["process", "processes"].includes(normalized)) return "processes";
  if ([
    "culture_and_climate",
    "climate_and_culture",
    "culture",
    "culture_and__climate",
  ].includes(normalized)) return "culture_and_climate";
  return normalized || "";
};

export const formatFactorArea = (value) => {
  const key = normalizeAreaValue(value);
  if (key === "culture_and_climate") return "Culture & Climate";
  return AREA_LABELS[key] || areaLabel(key);
};

const normalizeAreas = (payload = {}) => {
  const rawAreas = pickFirst(payload, ["areas", "area_summary", "areaSummary"]);

  if (Array.isArray(rawAreas)) {
    return rawAreas.map((area) => {
      const key = normalizeAreaKey(area);
      return {
        key,
        label: pickFirst(area, ["label", "display_name", "displayName"]) || areaLabel(key),
        mean: toNumber(pickFirst(area, ["mean", "overall_mean", "overallMean"])),
        stabilityIndex: toNumber(pickFirst(area, ["stability_index", "stabilityIndex"])),
      };
    });
  }

  if (rawAreas && typeof rawAreas === "object") {
    return Object.entries(rawAreas).map(([key, area]) => ({
      key,
      label: pickFirst(area, ["label", "display_name", "displayName"]) || areaLabel(key),
      mean: toNumber(pickFirst(area, ["mean", "overall_mean", "overallMean"])),
      stabilityIndex: toNumber(pickFirst(area, ["stability_index", "stabilityIndex"])),
    }));
  }

  return [];
};

const normalizeFactor = (factor = {}) => ({
  id: pickFirst(factor, ["id", "survey_page_id", "surveyPageId", "factor_id", "factorId"]),
  name: pickFirst(factor, ["name", "factor", "factor_name", "factorName", "title"]) || "Factor",
  area: pickFirst(factor, ["area", "area_name", "areaName"]),
  mean: toNumber(pickFirst(factor, ["mean", "overall_mean", "overallMean"])),
  stabilityIndex: toNumber(pickFirst(factor, ["stability_index", "stabilityIndex"])),
});

const normalizeFactors = (payload = {}, keys = []) => {
  const factors = pickFirst(payload, keys);
  return Array.isArray(factors) ? factors.map(normalizeFactor) : [];
};

const normalizeOrganizationStatusCounts = (payload = {}) => {
  const counts = pickFirst(payload, [
    "organization_status_counts",
    "organizationStatusCounts",
  ]);
  const emptyCounts = {
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    noSurvey: 0,
  };

  if (counts === null) return emptyCounts;
  if (typeof counts !== "object" || Array.isArray(counts)) {
    return Object.fromEntries(Object.keys(emptyCounts).map((key) => [key, null]));
  }

  const normalizeCount = (keys) => {
    const value = pickFirst(counts, keys);
    return value === null ? 0 : toNumber(value);
  };

  return {
    completed: normalizeCount(["completed"]),
    inProgress: normalizeCount(["in_progress", "inProgress"]),
    notStarted: normalizeCount(["not_started", "notStarted"]),
    noSurvey: normalizeCount(["no_survey", "noSurvey"]),
  };
};

const parseCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const normalizeStatus = (value) => {
  const status = (value || "").toString().trim().toLowerCase();
  if (["completed", "complete", "done"].includes(status)) return "completed";
  if (["in_progress", "in progress", "active", "started"].includes(status)) return "in_progress";
  if (["not_started", "not started", "pending"].includes(status)) return "not_started";
  if (["no_survey", "no survey", "missing"].includes(status)) return "no_survey";
  return status || "no_survey";
};

const normalizeClassification = (value) => {
  const classification = (value || "").toString().trim().toLowerCase();
  if (["strength", "strong", "positive"].includes(classification)) return "strength";
  if (["development", "development_area", "development area", "opportunity", "risk"].includes(classification)) return "development";
  return "neutral";
};

const getOrganization = (item = {}) => item.organization || item.org || item;

const getSurvey = (item = {}) =>
  item.organization_survey || item.organizationSurvey || item.survey || {};

export const formatNumber = (value, digits = 2) => {
  const number = toNumber(value);
  return number === null ? "N/A" : number.toFixed(digits);
};

export const formatInteger = (value) => {
  const number = toNumber(value);
  return number === null ? "N/A" : Math.round(number).toLocaleString();
};

export const formatPercent = (value) => {
  const number = normalizePercent(value);
  return number === null ? "N/A" : `${Math.round(number)}%`;
};

export const normalizeNetworkReportSummary = (payload = {}) => {
  const summary = payload?.summary || payload?.report_summary || payload;
  const overall = summary?.overall || {};

  const normalized = {
    networkName: getNetworkName(summary),
    organizationsCount: toNumber(pickFirst(summary, [
      "organizations_count",
      "organization_count",
      "organizationsCount",
      "organizationCount",
    ])),
    totalRespondents: toNumber(pickFirst(summary, [
      "total_respondents",
      "respondents_count",
      "respondentsCount",
      "totalRespondents",
    ])),
    completedRespondents: toNumber(pickFirst(summary, [
      "completed_respondents",
      "completedRespondents",
      "completed_responses",
      "completedResponses",
    ])),
    completionRate: normalizePercent(pickFirst(summary, [
      "completion_rate",
      "completionRate",
      "response_rate",
      "responseRate",
    ])),
    organizationStatusCounts: normalizeOrganizationStatusCounts(summary),
    averageOrganizationCompletionRate: normalizePercent(pickFirst(summary, [
      "average_organization_completion_rate",
      "averageOrganizationCompletionRate",
    ])),
    overallStabilityIndex: toNumber(pickFirst(summary, [
      "overall_stability_index",
      "overallStabilityIndex",
      "stability_index",
      "stabilityIndex",
    ]) ?? pickFirst(overall, ["stability_index", "stabilityIndex"])),
    overallMean: toNumber(pickFirst(summary, [
      "overall_mean",
      "overallMean",
      "mean",
    ]) ?? pickFirst(overall, ["mean"])),
    areas: normalizeAreas(summary),
    strongestFactors: normalizeFactors(summary, [
      "strongest_factors",
      "strongestFactors",
      "strengths",
      "top_factors",
      "topFactors",
    ]),
    developmentFactors: normalizeFactors(summary, [
      "development_factors",
      "developmentFactors",
      "development_areas",
      "developmentAreas",
      "bottom_factors",
      "bottomFactors",
    ]),
  };

  normalized.hasData = [
    normalized.organizationsCount,
    normalized.totalRespondents,
    normalized.completedRespondents,
    normalized.completionRate,
    normalized.averageOrganizationCompletionRate,
    normalized.overallStabilityIndex,
    normalized.overallMean,
  ].some((value) => value !== null) ||
    normalized.areas.length > 0 ||
    normalized.strongestFactors.length > 0 ||
    normalized.developmentFactors.length > 0;

  return normalized;
};

export const normalizeNetworkReportOrganizations = (payload = {}) =>
  parseCollection(payload, ["organizations", "network_organizations", "results"]).map((item, index) => {
    const organization = getOrganization(item);
    const survey = getSurvey(item);
    const overall = item.overall || {};
    const reportLinks = item.report_links || item.reportLinks || {};
    const organizationSurveyId = pickFirst(item, [
      "organization_survey_id",
      "organizationSurveyId",
      "survey_id",
      "surveyId",
      "report_id",
      "reportId",
    ]) || pickFirst(survey, ["organization_survey_id", "organizationSurveyId", "id"]);

    const organizationId = pickFirst(item, [
      "organization_id",
      "organizationId",
    ]) || pickFirst(organization, ["id"]) || pickFirst(item, ["id"]);

    const completionRate = normalizePercent(pickFirst(item, [
      "completion_rate",
      "completionRate",
      "response_rate",
      "responseRate",
    ]));

    return {
      id: organizationId || organizationSurveyId || `organization-${index}`,
      organizationId,
      organizationName: pickFirst(item, [
        "organization_name",
        "organizationName",
        "name",
      ]) || pickFirst(organization, ["name"]) || "School",
      organizationSurveyId,
      surveyId: pickFirst(item, ["survey_id", "surveyId"]) || pickFirst(survey, ["id"]),
      surveyName: pickFirst(item, [
        "survey_name",
        "surveyName",
        "organization_survey_name",
        "organizationSurveyName",
      ]) || pickFirst(survey, ["name", "title"]),
      surveyPeriodId: pickFirst(item, ["survey_period_id", "surveyPeriodId"]),
      surveyPeriodKey: pickFirst(item, ["survey_period_key", "surveyPeriodKey"]),
      surveyPeriodName: pickFirst(item, ["survey_period_name", "surveyPeriodName"]),
      surveyPeriodStartDate: pickFirst(item, [
        "survey_period_start_date",
        "surveyPeriodStartDate",
      ]),
      surveyPeriodEndDate: pickFirst(item, [
        "survey_period_end_date",
        "surveyPeriodEndDate",
      ]),
      status: normalizeStatus(pickFirst(item, [
        "status",
        "survey_status",
        "surveyStatus",
        "report_status",
        "reportStatus",
      ]) || pickFirst(survey, ["status"])),
      completionRate,
      totalRespondents: toNumber(pickFirst(item, [
        "total_respondents",
        "totalRespondents",
        "respondent_count",
        "respondentCount",
        "respondents_count",
        "respondentsCount",
      ])),
      completedRespondents: toNumber(pickFirst(item, [
        "completed_respondents",
        "completedRespondents",
        "completed_respondent_count",
        "completedRespondentCount",
        "completed_responses",
        "completedResponses",
      ])),
      overallStabilityIndex: toNumber(pickFirst(item, [
        "overall_stability_index",
        "overallStabilityIndex",
        "stability_index",
        "stabilityIndex",
      ]) ?? pickFirst(overall, ["stability_index", "stabilityIndex"])),
      reportAvailable: toBoolean(pickFirst(item, [
        "report_available",
        "reportAvailable",
        "has_report",
        "hasReport",
      ]) ?? pickFirst(reportLinks, ["organization_report", "organizationReport"])),
      workbookAvailable: toBoolean(pickFirst(item, [
        "workbook_available",
        "workbookAvailable",
        "has_workbook",
        "hasWorkbook",
      ]) ?? pickFirst(reportLinks, ["workbook"])),
      strengths: normalizeFactors(item, [
        "strengths",
        "strongest_factors",
        "strongestFactors",
        "top_factors",
        "topFactors",
      ]),
      developmentAreas: normalizeFactors(item, [
        "development_areas",
        "developmentAreas",
        "development_factors",
        "developmentFactors",
        "bottom_factors",
        "bottomFactors",
      ]),
    };
  });

export const normalizeNetworkReportFactors = (payload = {}) =>
  parseCollection(payload, [
    "factors",
    "factor_rankings",
    "factorRankings",
    "rankings",
    "results",
  ]).map((item, index) => {
    const factor = item.factor || item.survey_page || item.surveyPage || item;
    const overall = item.overall || factor.overall || {};
    const rawArea = pickFirst(item, [
      "area",
      "area_name",
      "areaName",
    ]) || pickFirst(factor, ["area", "area_name", "areaName"]);
    const areaKey = normalizeAreaValue(rawArea);

    return {
      id: pickFirst(item, [
        "id",
        "survey_page_id",
        "surveyPageId",
        "factor_id",
        "factorId",
      ]) || pickFirst(factor, ["id"]) || `factor-${index}`,
      rank: toNumber(pickFirst(item, ["rank", "position"])) ?? index + 1,
      name: pickFirst(item, [
        "name",
        "factor",
        "factor_name",
        "factorName",
        "title",
      ]) || pickFirst(factor, ["name", "factor", "title"]) || "Factor",
      areaKey,
      areaLabel: formatFactorArea(areaKey),
      stabilityIndex: toNumber(pickFirst(item, [
        "stability",
        "stability_index",
        "stabilityIndex",
        "overall_stability_index",
        "overallStabilityIndex",
      ]) ?? pickFirst(overall, ["stability_index", "stabilityIndex"])),
      organizationsIncluded: toNumber(pickFirst(item, [
        "organizations_included",
        "organizationsIncluded",
        "organization_count",
        "organizationCount",
      ])),
      respondents: toNumber(pickFirst(item, [
        "respondents",
        "respondent_count",
        "respondentCount",
        "respondents_count",
        "respondentsCount",
        "total_respondents",
        "totalRespondents",
      ])),
      classification: normalizeClassification(pickFirst(item, [
        "classification",
        "category",
        "status",
      ])),
    };
  });

export const normalizeNetworkReportFactorMatrix = (payload = {}) => {
  const matrix = payload?.factor_matrix || payload?.factorMatrix || payload;
  const categories = parseCollection(matrix, ["categories"]).map((category) => ({
    key: pickFirst(category, ["key", "category_key", "categoryKey"])?.toString() || "",
    name: pickFirst(category, ["name", "label", "category_name", "categoryName"]) || "Category",
    factors: parseCollection(category, ["factors"]).map((factor, index) => ({
      id: pickFirst(factor, ["id", "factor_id", "factorId", "survey_page_id", "surveyPageId"])
        ?? `factor-${index}`,
      name: pickFirst(factor, ["name", "factor_name", "factorName", "title"]) || "Factor",
    })),
  }));

  const schools = parseCollection(matrix, ["schools", "organizations", "results"]).map((school, index) => ({
    id: pickFirst(school, ["organization_id", "organizationId", "id"]) ?? `school-${index}`,
    organizationId: pickFirst(school, ["organization_id", "organizationId", "id"]),
    organizationName: pickFirst(school, ["organization_name", "organizationName", "name"]) || "School",
    organizationSurveyId: pickFirst(school, ["organization_survey_id", "organizationSurveyId"]),
    overallStabilityIndex: toNumber(pickFirst(school, [
      "overall_stability_index",
      "overallStabilityIndex",
    ])),
    factorResults: parseCollection(school, ["factor_results", "factorResults"]).map((result) => ({
      factorId: pickFirst(result, ["factor_id", "factorId", "survey_page_id", "surveyPageId", "id"]),
      stabilityIndex: toNumber(pickFirst(result, ["stability_index", "stabilityIndex"])),
    })),
  }));

  return {
    networkId: pickFirst(matrix, ["network_id", "networkId"]),
    categories,
    schools,
  };
};
